/**
 * Module licensing: which product modules may this tenant open?
 *
 * Pure functions over the module catalog (GET /auth/api/v1/rbac/catalog, or
 * the generated fallback in config/modules.generated.js) and the org's
 * licensed slugs (the JWT `modules` claim, read by utils/entitlements.js).
 * They mirror saf3ai_auth.entitlement in the auth SDK so the UI and every
 * service agree:
 *
 * - A slug or any of its former slugs (aliases) names the same module.
 * - Core modules (catalog `core: true`) are always licensed.
 * - A platform super admin (no organization_id, or the Super Admin role) is
 *   never locked.
 * - A tenant with an empty module list holds the core modules and nothing
 *   else. (The sidebar used to read an empty list as "show everything" while
 *   ProtectedRoute read it as "deny everything"; both now read it this way.)
 * - An unknown slug licenses nothing, and a page that requires one is locked.
 * - `ui_module_lock: false` from the auth service switches all of this off:
 *   nothing is locked, as before the feature.
 *
 * Locking is presentation. What keeps an unlicensed tenant's data out of the
 * browser is that a locked page is never mounted (so it never fetches) plus
 * the services' own 403 module_not_licensed. See components/ModuleLocked.
 */

const norm = (s) => (s == null ? '' : String(s).trim().toLowerCase());
/** Slug form, as the SDK's canonical_module: case, space and hyphens ignored. */
const slugKey = (s) => norm(s).replace(/-/g, '_');

/** Index a raw catalog (the service's JSON shape) for lookups. */
export function indexCatalog(raw) {
  const modules = new Map();
  const aliases = new Map();
  const core = new Set();
  const sections = new Map();
  const list = Array.isArray(raw?.modules) ? raw.modules : [];
  list.forEach((m) => {
    const slug = slugKey(m?.slug);
    if (!slug) return;
    modules.set(slug, {
      slug,
      label: m.label || slug,
      group: m.group || null,
      core: Boolean(m.core),
      aliases: (m.aliases || []).map(slugKey).filter(Boolean),
      uiSections: (m.ui_sections || []).map(String),
    });
  });
  modules.forEach((m) => {
    if (m.core) core.add(m.slug);
    m.aliases.forEach((a) => { if (!modules.has(a)) aliases.set(a, m.slug); });
    m.uiSections.forEach((key) => {
      if (!sections.has(key)) sections.set(key, []);
      sections.get(key).push(m.slug);
    });
  });
  return {
    modules,
    aliases,
    core,
    sections,
    groups: Array.isArray(raw?.module_groups) ? raw.module_groups : [],
  };
}

/** The current slug for `slug` (itself or what an alias became); null when unknown. */
export function canonicalModule(slug, index) {
  const key = slugKey(slug);
  if (!key) return null;
  if (index.modules.has(key)) return key;
  return index.aliases.get(key) || null;
}

/** Canonical slugs a `modules` claim grants, plus every core module. */
export function licensedModuleSet(slugs, index) {
  const out = new Set(index.core);
  (Array.isArray(slugs) ? slugs : []).forEach((s) => {
    const c = canonicalModule(s, index);
    if (c) out.add(c);
  });
  return out;
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

const SUPER_ADMIN = 'super admin';

/**
 * A platform super admin. The services skip the licensing check for a token
 * with no organization_id; the UI also honours the role name, which is what
 * the sidebars have always keyed on.
 */
export function isPlatformSuperAdmin(user, jwtPayload) {
  const u = user || {};
  const roleNames = [u.role_name, u.role_key];
  (Array.isArray(u.roles) ? u.roles : []).forEach((r) => {
    if (typeof r === 'string') roleNames.push(r);
    else if (r) roleNames.push(r.role, r.name);
  });
  if (roleNames.some((n) => norm(n).replace(/[_-]+/g, ' ') === SUPER_ADMIN)) return true;
  // No organization anywhere — not in the token, not on the stored user — is
  // the platform account the services never licence-check.
  const orgFromToken = jwtPayload && typeof jwtPayload === 'object' ? jwtPayload.organization_id : null;
  const orgFromUser = u.organization_id ?? u.org_id ?? u.organizationId;
  return orgFromToken == null && orgFromUser == null;
}

/**
 * Everything a caller needs to decide what is locked, as one snapshot.
 *
 *   index        indexCatalog(...) result
 *   lockEnabled  the auth service's ui_module_lock
 *   slugs        the org's licensed slugs (JWT `modules` claim)
 *   user         the signed-in user (localStorage `user`, normalised)
 *   token        the JWT, for the super-admin rule
 */
export function makeModuleLicense({ index, lockEnabled = true, slugs = [], user = {}, token = null, ready = true } = {}) {
  const superAdmin = isPlatformSuperAdmin(user, decodeJwtPayload(token));
  const licensed = licensedModuleSet(slugs, index);
  const enforcing = Boolean(lockEnabled) && !superAdmin;

  const isLicensed = (slug) => {
    if (!enforcing) return true;
    const c = canonicalModule(slug, index);
    if (!c) return false;
    return index.core.has(c) || licensed.has(c);
  };
  const isAnyLicensed = (slugs2) => {
    const list = (Array.isArray(slugs2) ? slugs2 : [slugs2]).filter((s) => norm(s));
    if (list.length === 0) return true;
    return list.some(isLicensed);
  };
  const sectionModules = (sectionKey) => index.sections.get(sectionKey) || [];
  /** A sidebar section is licensed when it belongs to no module, or to one the org holds. */
  const isSectionLicensed = (sectionKey) => {
    const mods = sectionModules(sectionKey);
    return mods.length === 0 || mods.some(isLicensed);
  };
  const moduleLabel = (slug) => {
    const c = canonicalModule(slug, index);
    return (c && index.modules.get(c)?.label) || (slug ? String(slug) : '');
  };
  /** The first module of `slugs` the org lacks — what a lock screen names. */
  const firstLocked = (slugs2) => {
    const list = (Array.isArray(slugs2) ? slugs2 : [slugs2]).filter((s) => norm(s));
    if (list.length === 0 || list.some(isLicensed)) return null;
    return canonicalModule(list[0], index) || norm(list[0]);
  };

  return {
    ready,
    lockEnabled: Boolean(lockEnabled),
    isSuperAdmin: superAdmin,
    enforcing,
    licensed,
    isLicensed,
    isAnyLicensed,
    isSectionLicensed,
    sectionModules,
    moduleLabel,
    firstLocked,
    canonical: (slug) => canonicalModule(slug, index),
    /** Core modules (Atlas, Policy, …) are in every plan and never lock. */
    isCore: (slug) => {
      const c = canonicalModule(slug, index);
      return Boolean(c) && index.core.has(c);
    },
  };
}
